import type { AiOperationalRequest, AiWorkspaceMemoryInsight } from '../../../types/aiOperational'
import type { AiOperationalContextWindow, AiRetrievedRecord } from '../context/operationalRetrieval'

export interface WorkspaceOperationalMemoryInput {
  request: AiOperationalRequest
  context: AiOperationalContextWindow
  nowIso?: string
  maxInsights?: number
}

const clampConfidence = (value: number) => Math.max(0.1, Math.min(0.95, Number(value.toFixed(2))))

const stableId = (prefix: string, parts: string[]) =>
  `${prefix}_${parts.join('_').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 96)}`

const byReason = (records: AiRetrievedRecord[], reason: string) =>
  records.filter((record) => record.reasons.includes(reason))

const activeRecords = (records: AiRetrievedRecord[]) =>
  records.filter((record) => record.status === 'IN_PROGRESS' || record.status === 'ONGOING')

const createInsight = (
  patch: Omit<AiWorkspaceMemoryInsight, 'visibility' | 'generatedAtIso'>,
  generatedAtIso: string,
): AiWorkspaceMemoryInsight => ({
  ...patch,
  confidence: clampConfidence(patch.confidence),
  visibility: 'WORKSPACE_SAFE',
  generatedAtIso,
})

export const buildWorkspaceOperationalMemory = (input: WorkspaceOperationalMemoryInput): AiWorkspaceMemoryInsight[] => {
  const generatedAtIso = input.nowIso ?? new Date().toISOString()
  const records = [...input.context.primary, ...input.context.supporting]
  const insights: AiWorkspaceMemoryInsight[] = []
  const dependencyRecords = byReason(records, 'dependency participant')
  const gatedRecords = byReason(records, 'completion gate')
  const active = activeRecords(records)
  const pulseRisks = input.context.pulseHints.filter((hint) => /critical|watch|risk|block|shortage|fail/i.test(`${hint.severity ?? ''} ${hint.summary}`))

  if (dependencyRecords.length > 0) {
    insights.push(createInsight({
      id: stableId('mem_dependency', dependencyRecords.slice(0, 4).map((record) => record.id)),
      kind: 'DEPENDENCY_PATTERN',
      summary: `Dependency-sensitive work is in scope; verify blockers before proposing completion or status changes for ${dependencyRecords.slice(0, 3).map((record) => record.title).join(', ')}.`,
      evidenceRecordIds: dependencyRecords.slice(0, 6).map((record) => record.id),
      confidence: 0.82,
      retentionDays: 30,
      source: 'RETRIEVAL',
    }, generatedAtIso))
  }

  if (gatedRecords.length > 0) {
    insights.push(createInsight({
      id: stableId('mem_completion_gate', gatedRecords.slice(0, 4).map((record) => record.id)),
      kind: 'COMPLETION_CONSTRAINT',
      summary: `Some checklist work has proof, location, or other completion gates; do not mark it complete without checking the existing gates.`,
      evidenceRecordIds: gatedRecords.slice(0, 6).map((record) => record.id),
      confidence: 0.86,
      retentionDays: 30,
      source: 'RETRIEVAL',
    }, generatedAtIso))
  }

  if (active.length >= 2) {
    insights.push(createInsight({
      id: stableId('mem_active_work', active.slice(0, 4).map((record) => record.id)),
      kind: 'WORKLOAD_SIGNAL',
      summary: `Multiple retrieved records are already active; prefer sequencing and drift checks before adding new parallel work.`,
      evidenceRecordIds: active.slice(0, 6).map((record) => record.id),
      confidence: 0.72,
      retentionDays: 14,
      source: 'RETRIEVAL',
    }, generatedAtIso))
  }

  if (pulseRisks.length > 0) {
    insights.push(createInsight({
      id: stableId('mem_pulse_risk', pulseRisks.slice(0, 4).map((hint) => hint.id)),
      kind: 'RISK_PATTERN',
      summary: `Recent Pulse signals indicate operational risk: ${pulseRisks.slice(0, 2).map((hint) => hint.summary).join(' ')}`,
      evidenceRecordIds: pulseRisks.map((hint) => hint.targetId).filter((id): id is string => Boolean(id)).slice(0, 6),
      confidence: 0.78,
      retentionDays: 14,
      source: 'PULSE',
    }, generatedAtIso))
  }

  if (/\b(save time|quick|brief|high-level|checklist|detailed|step by step|step-by-step)\b/i.test(input.request.prompt)) {
    insights.push(createInsight({
      id: stableId('mem_request_style', [input.request.mode, input.request.surface]),
      kind: 'OPERATING_PATTERN',
      summary: 'The current request contains an explicit communication or detail-level preference; honor it for this operation without making it a permanent user profile.',
      evidenceRecordIds: [],
      confidence: 0.62,
      retentionDays: 1,
      source: 'REQUEST',
    }, generatedAtIso))
  }

  return insights
    .sort((left, right) => right.confidence - left.confidence || left.kind.localeCompare(right.kind))
    .slice(0, input.maxInsights ?? 6)
}
